// HIGHLIGHTS — what the product page says about each series.
//
// One entry per series, because every capacity in a series shares the same
// features. The product page shows the heat figure first (the question every
// Delhi buyer asks is "will it cool in June?"), then three highlights.
//
// HEAT FIGURES come from Daikin India's own spec page for each series
// (daikinindia.com, "100% Cooling Capacity Temperature" and "Ambient
// Temperature Range, Cooling Max"). Checked Oct 2026:
//
//   GTQ  (FTQ)   cools to 50°C, full capacity at 43°C
//   GTKC (FTKC)  cools to 52°C, full capacity at 43°C
//   GTKL (FTKL)  cools to 52°C, full capacity at 43°C
//   FTHT         cools to 54°C, full capacity at 43°C, heats down to -10°C
//   FTKU         cools to 55°C, full capacity at 46°C
//   GTKM (FTKM)  cools to 55°C, full capacity at 46°C
//   JTKJ         cools to 55°C, full capacity at 46°C
//
// If Daikin revises a figure, change it here and every page in that series
// follows. Never write a figure that is not on Daikin's spec page.
//
// Icons are names from the ICONS map in pages/ProductDetail.jsx.

export const SERIES = {
  GTQ: {
    tagline: "Daikin's lowest-priced AC. Fixed speed, best for rooms used now and then.",
    heat: { max: 50, full: 43 },
    highlights: [
      { icon: 'gauge', title: 'Econo mode', text: "Caps the AC's power draw when you want to keep the load or the bill down." },
      { icon: 'drop', title: 'Dry mode', text: 'Pulls humidity out in the monsoon without over-cooling the room.' },
      { icon: 'shield', title: 'Smell-proof', text: 'Stops musty smells building up inside the indoor unit.' },
    ],
  },
  GTKC: {
    tagline: "Daikin's best-priced inverter AC, with a voltage stabiliser built in.",
    heat: { max: 52, full: 43 },
    highlights: [
      { icon: 'bolt', title: 'Stabiliser built in', text: 'Handles voltage swings, so there is no separate stabiliser to buy.' },
      { icon: 'wave', title: 'Inverter', text: 'Slows down instead of switching off, so the room stays steady and bills stay lower.' },
      { icon: 'drop', title: 'Dew Clean', text: 'Washes the indoor coil with its own condensed water for fresher air.' },
    ],
  },
  GTKL: {
    tagline: 'Premium build with PM2.5 and active carbon filters for cleaner room air.',
    heat: { max: 52, full: 43 },
    highlights: [
      { icon: 'filter', title: 'PM2.5 filter', text: "Catches fine dust and smoke particles from the room's air." },
      { icon: 'carbon', title: 'Active carbon filter', text: 'Helps take cooking and other odours out of the room.' },
      { icon: 'chill', title: 'Power Chill', text: 'Cools the room 20% faster than normal mode when you walk in hot.' },
    ],
  },
  FTHT: {
    tagline: 'Cools in summer, heats in winter. One unit for the whole Delhi year.',
    heat: { max: 54, full: 43 },
    heatingMin: -10,
    highlights: [
      { icon: 'thermo', title: 'Heats down to -10°C outside', text: 'Runs as a heat pump in winter and replaces a room heater, for far less to run.' },
      { icon: 'filter', title: 'PM1.0 filter', text: 'Catches even finer particles than a PM2.5 filter.' },
      { icon: 'wave', title: 'Inverter', text: 'Slows down instead of switching off, so the room stays steady and bills stay lower.' },
    ],
  },
  FTKU: {
    tagline: '5 Star running cost at a lower price, with a PM2.5 filter.',
    heat: { max: 55, full: 46 },
    highlights: [
      { icon: 'leaf', title: '5 Star, ISEER 5.7', text: 'Uses roughly a quarter less electricity than a 3 Star unit of the same size.' },
      { icon: 'filter', title: 'PM2.5 filter', text: "Catches fine dust and smoke particles from the room's air." },
      { icon: 'drop', title: 'Dew Clean', text: 'Washes the indoor coil with its own condensed water for fresher air.' },
    ],
  },
  GTKM: {
    tagline: "Daikin's premium 5 Star: the lowest running cost, cleaner air, and rated to 55°C.",
    heat: { max: 55, full: 46 },
    highlights: [
      { icon: 'leaf', title: '5 Star, ISEER 5.7', text: 'Uses roughly a quarter less electricity than a 3 Star unit of the same size.' },
      { icon: 'filter', title: 'PM2.5 filter', text: "Catches fine dust and smoke particles from the room's air." },
      { icon: 'carbon', title: 'Active carbon filter', text: 'Helps take cooking and other odours out of the room.' },
    ],
  },
  JTKJ: {
    tagline: "Top of the range: WiFi and voice control, with Daikin's Streamer purification.",
    heat: { max: 55, full: 46 },
    highlights: [
      { icon: 'wifi', title: 'WiFi and voice control', text: 'Switch it on from your phone before you get home, or by voice through a smart speaker.' },
      { icon: 'eye', title: 'Intelligent Eye', text: 'Senses when the room is empty and eases off to save power.' },
      { icon: 'filter', title: 'Streamer purification', text: "Daikin's patented Streamer breaks down bacteria and allergens in the air passing through." },
    ],
  },
};

// A series not listed above still gets a sensible page: its catalogue blurb as
// the tagline and its catalogue features as highlights, with no heat claim.
export function seriesInfo(model) {
  const s = SERIES[model.series];
  if (s) return s;
  return {
    tagline: model.blurb || '',
    heat: null,
    highlights: (model.features || []).slice(0, 3).map((f) => ({ icon: 'check', title: f, text: '' })),
  };
}